import { useState } from 'react';
import { ImagePlus, Loader2, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { BRAND_COLOR_OPTIONS } from '../../../lib/brandColors';
import { useHesapBranding } from '../../../store/hesapBrandingStore';
import { RUNTIME_BACKEND_URL } from '../../../lib/backendUrl';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const LogoField = ({ token }) => {
  const logoUrl = useHesapBranding(s => s.logoUrl);
  const [busy, setBusy] = useState(false);

  const call = async (method, body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/site-settings/logo`, { method, headers: { Authorization: `Bearer ${token}` }, body });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'İşlem başarısız');
      await useHesapBranding.getState().load();
      toast.success(okMsg);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    call('POST', fd, 'Logo yüklendi');
  };

  return (
    <div>
      <Label className="text-xs">Logo (uygulama ikonu, sekme ikonu, menü)</Label>
      <div className="flex items-center gap-3 mt-1">
        <div className="w-14 h-14 rounded-xl border bg-white flex items-center justify-center overflow-hidden" data-testid="site-logo-preview">
          {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" /> : <ImagePlus className="h-5 w-5 text-slate-400" />}
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {logoUrl ? 'Değiştir' : 'Logo Yükle'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy}
            onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="site-logo-input" />
        </label>
        {logoUrl && (
          <Button type="button" variant="ghost" size="sm" onClick={() => call('DELETE', undefined, 'Logo kaldırıldı')} disabled={busy} className="text-red-600" data-testid="site-logo-remove-btn">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <p className="text-[10px] text-slate-500 mt-1">Kare (ör. 512×512) PNG önerilir. Yüklenince otomatik uygulanır.</p>
    </div>
  );
};

const ColorPicker = ({ value, onChange }) => (
  <div>
    <Label className="text-xs">Marka Rengi (menü, butonlar, uygulama çubuğu)</Label>
    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-2" data-testid="brand-color-picker">
      {BRAND_COLOR_OPTIONS.map(c => (
        <button key={c.key} type="button" title={c.label} onClick={() => onChange(c.key)}
          className={`relative h-10 rounded-lg ring-offset-2 transition-transform hover:scale-105 ${value === c.key ? 'ring-2 ring-slate-900 dark:ring-white' : ''}`}
          style={{ backgroundColor: c.hex }} data-testid={`brand-color-${c.key}`}>
          {value === c.key && <Check className="h-4 w-4 text-white absolute inset-0 m-auto" />}
        </button>
      ))}
    </div>
    <p className="text-[10px] text-slate-500 mt-1">Seçtiğiniz renk anında önizlenir; kalıcı olması için Kaydet'e basın.</p>
  </div>
);

export const GeneralTab = ({ value, onChange, token }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Site Başlığı (sekme, uygulama adı, menü)</Label>
          <Input value={value.site_title || ''} onChange={e => set('site_title', e.target.value)} placeholder="Akıllı Çözüm Merkezi" data-testid="site-title-input" />
        </div>
        <LogoField token={token} />
      </div>
      <ColorPicker value={value.brand_color || 'emerald'} onChange={k => { useHesapBranding.getState().previewColor(k); set('brand_color', k); }} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Alt Başlık (menü, giriş, tanıtım sayfası)</Label>
          <Input value={value.subtitle || ''} onChange={e => set('subtitle', e.target.value)} placeholder="POS · Muhasebe Sistemi" data-testid="site-subtitle-input" />
        </div>
        <div>
          <Label className="text-xs">Footer Metni</Label>
          <Input value={value.footer_text || ''} onChange={e => set('footer_text', e.target.value)} placeholder="© 2026 Akıllı Çözüm Merkezi · Tüm hakları saklıdır" data-testid="site-footer-input" />
        </div>
      </div>
    </div>
  );
};
